<template>
  <v-app>
    <AppWrapperView>
      <v-main>
        <v-container class="pa-8" fluid>
          <!-- どの棚卸か。DAT の棚卸マスタ日は全行が棚卸日で同じなので、一覧ではなくここに出す（2026-10-28） -->
          <v-row v-if="sectionLayout">
            <v-col cols="12" class="tana_info">
              <div class="tana_info_item">
                <div class="section_title">{{ $t('tanaoroshiSai.label.infoShop') }}</div>
                <div class="tana_info_value">{{ tanaoroshi?.shop || shopCode }}</div>
              </div>
              <div class="tana_info_item">
                <div class="section_title">{{ $t('tanaoroshiSai.label.infoPlannedDate') }}</div>
                <div class="tana_info_value">{{ tanaoroshi?.plannedDate || "-" }}</div>
              </div>
            </v-col>
          </v-row>
          <v-row v-if="sectionLayout" class="no_print">
            <v-col cols="12">
              <div class="section_title">{{ $t('tanaoroshiSai.label.datTitle') }}</div>
              <div class="section_note" v-if="!datDownloadable && datList.length > 0">
                {{ $t('tanaoroshiSai.label.datNotSaved') }}
              </div>
              <v-table density="compact" class="text-no-wrap section_table">
                <thead>
                  <tr>
                    <th v-for="h in datHeaders" :key="h">
                      {{ $t('tanaoroshiSai.label.dat.' + h) }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="dat in datList" :key="dat.dayNo">
                    <td v-if="hasDatCol('no')">{{ dat.no }}</td>
                    <td v-if="hasDatCol('fileName')" class="file_name">{{ dat.fileName }}</td>
                    <td v-if="hasDatCol('dayNo')">
                      {{ $t('tanaoroshiSai.label.datDayNo', { day: dat.dayNo }) }}
                    </td>
                    <!-- 1日目・2日目を1ファイルにまとめる。何日分の作業が入っているかを出す -->
                    <td v-if="hasDatCol('workDays')">
                      {{ $t('tanaoroshiSai.label.datWorkDays', { date: dat.workDate, day: dat.dayNo }) }}
                    </td>
                    <td v-if="hasDatCol('masterDate')">{{ dat.masterDate }}</td>
                    <td v-if="hasDatCol('recordCount')">{{ formatCount(dat.recordCount) }}</td>
                    <td v-if="hasDatCol('sizeBytes')">{{ formatSize(dat.sizeBytes) }}</td>
                    <td v-if="hasDatCol('download')">
                      <v-btn
                        size="small"
                        variant="outlined"
                        color="indigo-darken-1"
                        :loading="datDownloadingDayNo === dat.dayNo"
                        :disabled="!dat.downloadable"
                        @click="downloadDat(dat)"
                      >{{ $t('tanaoroshiSai.action.download') }}</v-btn>
                    </td>
                  </tr>
                  <tr v-if="datList.length === 0">
                    <td :colspan="datHeaders.length">{{ $t('tanaoroshiSai.label.datEmpty') }}</td>
                  </tr>
                </tbody>
              </v-table>
            </v-col>
          </v-row>

          <v-row v-if="showTopFilters">
            <v-col cols="3" class="print_col" v-if="hasCol('zoneName')">
              <v-select
                density="compact"
                variant="outlined"
                hide-details="auto"
                :items="zoneList"
                :label="$t('tanaoroshiSai.label.targetZoneCode')"
                item-title="zoneName"
                item-value="zoneCode"
                v-model="searchCondition.targetZoneCode"
                clearable
              ></v-select>
            </v-col>
            <v-col cols="3" class="print_col" v-if="hasCol('diffSuuryo')">
              <v-select
                  density="compact"
                  variant="outlined"
                  hide-details="auto"
                  :items="[
                      {'key':1, 'label': $t('tanaoroshiSai.label.little')},
                      {'key':2, 'label': $t('tanaoroshiSai.label.excess')},
                      {'key':3, 'label': $t('tanaoroshiSai.label.matched')},
                      {'key':4, 'label': $t('tanaoroshiSai.label.hasDifference')},
                      ]"
                  :label="$t('tanaoroshiSai.label.difference')"
                  item-title="label"
                  item-value="key"
                  v-model="searchCondition.filterLoss"
                  clearable
              ></v-select>
            </v-col>
            <!-- 2セクション構成のときは棚卸結果一覧の見出しの下に置く -->
            <!-- JAN は部分一致。打つたびではなく「検索」（または Enter）で絞り込む（2026-10-26 修正指示書 S-24） -->
            <v-col cols="3" class="print_col" v-if="!sectionLayout">
              <v-text-field density="compact" variant="outlined" hide-details="auto" v-model="janCodeInput"
                :label="$t('tanaoroshiSai.label.janCode')" @keydown.enter="applyJanSearch" />
            </v-col>
            <v-col cols="auto" class="no_print" v-if="!sectionLayout">
              <v-btn text @click="clearJanSearch">{{ $t('common.action.clear') }}</v-btn>
              <v-btn theme="dark" class="ml-2 px-8 bg-grey-darken-3" elevation="2" @click="applyJanSearch">{{ $t('common.action.search') }}</v-btn>
            </v-col>
            <v-col cols="3" class="print_col" v-if="hasCol('productCode')">
              <v-text-field density="compact" variant="outlined" hide-details="auto"
                v-model="searchCondition.productNumber"
                :label="$t('tanaoroshiSai.label.productNumber')" />
            </v-col>
          </v-row>
          <v-row v-if="hasCol('planSuuryo')">
            <v-col cols="4" class="print_col">
              <v-switch class="no_print" v-model="showZeroInventory" color="blue" inset :label="$t('tanaoroshiSai.label.showZeroInventory')"></v-switch>
              <div class="print_only">{{ this.showZeroInventory ? $t('tanaoroshiSai.label.printShowZeroInventoryOn') : $t('tanaoroshiSai.label.printShowZeroInventoryOff') }}</div>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <div class="section_title" v-if="sectionLayout">
                {{ $t('tanaoroshiSai.label.resultTitle') }}
              </div>
              <v-row v-if="sectionLayout" class="mb-2">
                <v-col cols="3" class="print_col">
                  <v-text-field
                    density="compact"
                    variant="outlined"
                    hide-details="auto"
                    v-model="janCodeInput"
                    :label="$t('tanaoroshiSai.label.janCode')"
                    @keydown.enter="applyJanSearch"
                  />
                </v-col>
                <v-col cols="auto" class="no_print">
                  <v-btn text @click="clearJanSearch">{{ $t('common.action.clear') }}</v-btn>
                  <v-btn theme="dark" class="ml-2 px-8 bg-grey-darken-3" elevation="2" @click="applyJanSearch">{{ $t('common.action.search') }}</v-btn>
                </v-col>
              </v-row>
              <v-table
                density="compact"
                class="text-no-wrap"
                :class="{ section_table: sectionLayout }"
              >
                <thead>
                  <tr>
                    <template v-for="header in headers" :key="header">
                      <template v-if="header == 'adjustementSuuryo'">
                        <th class="no_print"  @click.stop="tableSort(header)" :style="cursorCheck(header)">{{ $t("tanaoroshiSai.label.table." + header) }}</th>
                      </template>
                      <template v-else>
                        <th @click.stop="tableSort(header)" :style="cursorCheck(header)">{{ $t("tanaoroshiSai.label.table." + header) }}</th>
                      </template>
                    </template>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="data in filteredDataList" :key="data.no">
                    <template v-for="header in headers" :key="header">
                      <template v-if="header == 'adjustementSuuryo'">
                        <td class="no_print">
                          <v-text-field density="compact" variant="outlined" hide-details="auto"
                            v-model="data.adjustmentQuantity"
                            @input="fixAdjustmentQuantity(data, $event)"
                             />
                        </td>
                      </template>
                      <template v-else>
                        <td>{{ data[header] }}</td>
                      </template>
                    </template>
                  </tr>
                  <tr>
                    <td :colspan="headers.length">
                      
                    </td>
                  </tr>
                </tbody>
              </v-table>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="3"></v-col>
            <v-col cols="6">
              <v-pagination
                v-model="currentPageNo"
                :length="maxPageNo"/>
            </v-col>
            <v-col cols="3">
              <v-row>
                <v-spacer></v-spacer>
                <v-col cols="10" class="d-flex justify-end">
                  <v-select
                    density="compact"
                    variant="outlined"
                    hide-details="auto"
                    v-model="displayPerPage"
                    :items="displayPerPageItem"
                    item-title="name"
                    item-value="value"
                    :label="$t('common.label.displayCountPerPage')"
                    @update:modelValue = "resetCondition()"
                    return-object/>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12" class="text-right">
                  {{displayContentNo.minNo}} - {{displayContentNo.maxNo}}{{ $t('common.label.displayedDataRange') }} / {{ sortedTable.length }}{{ $t('common.label.totalCountOf') }}
                </v-col>
              </v-row>

            </v-col>
          </v-row>

          <v-row v-if="actions.length > 0">
            <v-col cols="12" class="text-right">
              <v-btn
                v-if="canAdjustment && hasAction('save')"
                dark
                class="px-8 bg-indigo-darken-1 mr-5"
                elevation="2"
                @click="openAdjustmentDialog"
                :disabled="!hasAdjustment">{{ $t('tanaoroshiSai.action.save') }}
              </v-btn>
              <v-btn
                v-if="canAdjustment && hasAction('addJan')"
                dark
                class="px-8 bg-indigo-darken-1 mr-5"
                elevation="2"
                @click="openAddJanDialog">{{ $t('tanaoroshiSai.action.addJan') }}
              </v-btn>
              <v-btn
                v-if="hasAction('outputCsv')"
                dark
                class="px-8 bg-indigo-darken-1 mr-5"
                elevation="2"
                @click="downloadCsv"
                :disabled="dataList.length == 0">{{ $t('tanaoroshiSai.action.outputCsv') }}
              </v-btn>

              <v-btn
                v-if="hasAction('print')"
                dark
                class="px-8 bg-indigo-darken-1"
                elevation="2"
                @click="print"
                :disabled="dataList.length == 0">{{ $t('tanaoroshiSai.action.print') }}
              </v-btn>
            </v-col>
          </v-row>
        </v-container>
        <ErrorDialog v-if="dataError" :URL="previousURL"/>
        <!-- DATのダウンロードができなかったとき（それまでは何も出なかった。ソースレビュー C8） -->
        <MessageDialog v-if="datErrorDialog" :dialog-data="datErrorDialog" :handle-ok="() => (datErrorDialog = null)" />
        <TanaoroshiAdjustmentDialog v-if="adjustmentDialogVisible" :dialog-data="adjustmentDialog" :handle-ok="confirmAdjustmentOk"
        :handle-cancel="confirmAdjustmentCancel" />
        <TanaoroshiAddJanDialog v-if="addJanDialogVisible" :dialog-data="addJanDialog" :handle-ok="confirmAddJanOk"
        :handle-cancel="confirmAddJanCancel" />
      </v-main>
    </AppWrapperView>
  </v-app>
</template>

<script lang="js">
import Vue from "vue";
import AppWrapperView from "@/components/views/AppWrapperView";
import router from "@/router/index";
import Paths from "@/router/indexPaths";
import api from "@/apis/staff";
import store from "@/store";
import utils from "@/utils";
import ErrorDialog from "@/components/dialog/ErrorDialog";
import MessageDialog from "@/components/dialog/MessageDialog";
import TanaoroshiAdjustmentDialog from "@/components/dialog/TanaoroshiAdjustmentDialog";
import TanaoroshiAddJanDialog from "@/components/dialog/TanaoroshiAddJanDialog";
import constant from "@/utils/constant"
import TableSorter from "@/components/TableSorter";

export default {
  components: {AppWrapperView, ErrorDialog, MessageDialog, TanaoroshiAdjustmentDialog, TanaoroshiAddJanDialog},
  computed: {
    // １つ目は必ず不必要なゾーンなので、除外する
    zoneList() {
      const clonedZoneList = this.tanaoroshi?.zoneList.map(x => x) ?? [];
      clonedZoneList.shift();
      // APV在庫の場合はゾーンコードリストを表示しない
      const	nameerp = this.$t('tanaoroshiSai.label.nameerp');
      if(this.tanaoroshi?.inventoryMode == 2){
        return [];
      }
      return clonedZoneList;
    },
    
    //どの部分を表示しているのか
    displayContentNo(){
      const minNo = this.dataList.length != 0 ? 1 + (this.displayPerPage.value * (this.currentPageNo - 1)) : 0;
      const maxNo = this.displayPerPage.value * (this.currentPageNo);
      if(maxNo > this.sortedTable.length || this.displayPerPage.value == 0){
        return {minNo: minNo, maxNo: this.sortedTable.length};
      }
      return {minNo: minNo, maxNo: maxNo};
    },

    //最大ページ
    maxPageNo() {
      if(this.displayPerPage.value == 0) return 0;
      return Math.ceil(this.sortedTable.length/this.displayPerPage.value);
    },

    //ソート&日付変換後のデータ
    sortedTable() {
      let tempArray = this.dataList.map(x => x);
      if(this.sortState.target != ""){
        tempArray = tempArray.sort(TableSorter.getSorter(this.sortState.target, this.sortState.ascent));
      }

      // フィルター
      // ロケ
      if(this.searchCondition.targetZoneCode != null) {
        tempArray = tempArray.filter(x => x.zoneCode == this.searchCondition.targetZoneCode)
      }
      
      // 差異
      if(this.searchCondition.filterLoss != null) {
        // 過少 予定数 > 読み取り数
        if(this.searchCondition.filterLoss == 1) tempArray = tempArray.filter(x => x.planSuuryo > x.resultSuuryo)
        // 過剰 予定数 < 読み取り数
        if(this.searchCondition.filterLoss == 2) tempArray = tempArray.filter(x => x.planSuuryo < x.resultSuuryo)
        // 一致 予定数 = 読み取り数
        if(this.searchCondition.filterLoss == 3) tempArray = tempArray.filter(x => x.planSuuryo == x.resultSuuryo)
        // 誤差 予定数 != 読み取り数
        if(this.searchCondition.filterLoss == 4) tempArray = tempArray.filter(x => x.planSuuryo != x.resultSuuryo)
      }

      // JANコード（部分一致。良品計画様の JAN は先頭7桁の企業コードがほぼ同じなので、下の桁でも引けるように。
      // 「検索」で反映した値で絞る。2026-10-26 修正指示書 S-24）
      const janCode = (this.searchCondition.janCode ?? "").trim();
      if(janCode != "") {
        tempArray = tempArray.filter(x => (x.janCode ?? "").includes(janCode));
      }
      // 品番
      if(this.searchCondition.productNumber != "") {
        tempArray = tempArray.filter(x => x.productCode == this.searchCondition.productNumber);
      }
      // 0件データ非表示
      if (!this.showZeroInventory) {
        tempArray = tempArray.filter(x => x.planSuuryo != 0 || x.resultSuuryo != 0);
      }
      // 変換
      tempArray = tempArray.map((x, index) => {
        // noはなし
        x.no = index + 1;
        return x;
      })
      
      return tempArray;
    },
    canAdjustment() {
      return this.inventoryStatus == constant.INVENTORY_STATUS.SAVED;
    },
    hasAdjustment() {
      return this.dataList.some(e => e.adjustmentQuantity);
    },
    // ページ区切り後のデータ
    filteredDataList() {
      const tempArray = this.sortedTable;
      return tempArray.slice(this.displayContentNo.minNo-1, this.displayContentNo.maxNo);
    },
    // 棚卸実績データ出力の列。DATは良品計画様の基幹フォーマットなので、
    // 定義を持つテナントだけにこのブロックを出す。
    datHeaders() {
      return this.$getTableDef("TanaoroshiSaiPageDat");
    },
    // DAT出力があると画面が「データ出力」「棚卸結果一覧」の2セクションになる。
    // そのときだけ見出しを出し、JAN検索もどちらの表に効くのか分かる位置へ移す。
    sectionLayout() {
      return this.datHeaders.length > 0;
    },
    // 上の検索行に出すものが1つも無ければ行ごと消す
    showTopFilters() {
      return this.hasCol("zoneName") || this.hasCol("diffSuuryo")
          || this.hasCol("productCode") || !this.sectionLayout;
    },
    // 画面下部に出すボタン。列と違って表には出ないが、
    // テナントごとの出し分けは同じ定義ファイルにまとめている。
    actions() {
      return this.$getTableDef("TanaoroshiSaiPageAction");
    },
    // ヘッダー
    headers() {
      let def = this.$getTableDef("TanaoroshiSaiPage");
      if (!this.canAdjustment) {
        def = def.filter(e => e != "adjustementSuuryo");
      }
      return def;
    },

  },
  data: () => ({
    // paramより
    tanaoroshi: null,
    shopCode: null,
    dataError: false,
    // DATダウンロードの失敗を知らせるダイアログ { message, errorList }
    datErrorDialog: null,
    searchDatetime: null,
    dataList: [],

    searchCondition: {
      targetZoneCode: null, // ロケーション
      filterLoss : null, // 差異
      janCode: "",
      productNumber: ""
    },
    inventoryStatus: null,
    // JAN の入力中の値（「検索」で searchCondition.janCode に反映する。2026-10-26 S-24）
    janCodeInput: "",

    currentPageNo: 1,
    //１ページ表示数
    displayPerPage: {},
    displayPerPageItem: [],
    //ソートの状態
    sortState: {target: "", ascent: true},

    // エラー時の遷移先
    previousURL: Paths.TANAOROSHI,
    htSerialZoneMap: null,
    // 調整ダイアログ表示フラグ
    adjustmentDialogVisible: false,
    // 調整ダイアログに渡すデータ
    adjustmentDialog: {
    },
    // 棚卸追加フラグ
    addJanDialogVisible: false,
    // 棚卸追加ダイアログに渡すデータ
    addJanDialog: {
    },
    showZeroInventory: false,

    // 棚卸実績データ（DAT）出力
    datList: [],
    datDownloadable: false,
    datDownloadingDayNo: null
  }),

  methods: {

    // 列設定（plugins/setting/table/<company>.json）に含まれる列かどうか。
    // 表の列だけでなく、その列に対応する検索条件の出し分けにも使う。
    hasCol(name) {
      return this.headers.includes(name);
    },

    hasAction(name) {
      return this.actions.includes(name);
    },

    hasDatCol(name) {
      return this.datHeaders.includes(name);
    },

    formatCount(value) {
      return value == null ? "-" : Number(value).toLocaleString();
    },

    // DATは固定長なのでKB表示で足りる
    formatSize(bytes) {
      if (bytes == null) return "-";
      return Math.round(bytes / 1024).toLocaleString() + " KB";
    },

    // DAT 一覧と、画面の見出し（店舗・棚卸日）を読む。URL から開いたときの店舗もここで決まる
    loadDatList() {
      return api.post("/tanaoroshi/dat/list", { tanaoroshiId: this.tanaoroshi.tanaoroshiId })
        .then((res) => {
          if (res.data.status !== "0") return;
          const r = res.data.results;
          if (r.shopCode) {
            this.shopCode = this.shopCode || r.shopCode;
            this.tanaoroshi = {
              ...this.tanaoroshi,
              shop: r.shopName ? r.shopCode + "_" + r.shopName : r.shopCode,
              plannedDate: r.plannedDate,
            };
          }
          if (this.datHeaders.length === 0) return;
          this.datList = r.datList || [];
          this.datDownloadable = r.downloadable;
        })
        // 読めなくても画面は開く（店舗が決まらなければ呼び出し側でエラー表示）
        .catch(() => null);
    },

    // 「k日目まで」の DAT が返る（常にその日までの累計）。最後の日の行がいまの累計で、
    // それより前の日の行は確認用（2026-10-28）
    downloadDat(dat) {
      this.datDownloadingDayNo = dat.dayNo;
      api.post("/tanaoroshi/dat/download",
          { tanaoroshiId: this.tanaoroshi.tanaoroshiId, upToDay: dat.dayNo })
        .then((res) => {
          if (res.data.status !== "0") {
            this.datErrorDialog = { message: res.data.errorMessage, errorList: res.data.errorList ?? [] };
            return;
          }
          const r = res.data.results;
          utils.binaryDownload(r.contentBase64, r.fileName);
        })
        .catch((error) => {
          this.datErrorDialog = { message: utils.getErrorMsg(error.response, this.$t), errorList: [] };
        })
        .finally(() => {
          this.datDownloadingDayNo = null;
        });
    },

    fixAdjustmentQuantity(data, event) {
      const val = event.target.value;
      data.adjustmentQuantity = val.replace(/[^0-9]/g, '');
    },
    cursorCheck(value){
      return "cursor: pointer;"
    },

    // JAN の絞り込みを反映して1ページ目へ（2026-10-26 S-24）
    applyJanSearch() {
      this.searchCondition.janCode = this.janCodeInput ?? "";
      this.currentPageNo = 1;
    },
    clearJanSearch() {
      this.janCodeInput = "";
      this.applyJanSearch();
    },

    //テーブルの条件リセット
    resetCondition(){
      //１ページ目
      this.currentPageNo = 1;
      //既定の並び（読み直すたびにJAN順が外れて No. と CSV の並びが変わっていた。ソースレビュー C14）
      this.sortState.target = this.defaultSortTarget();
      this.sortState.ascent = true;
    },
    // 見出しで並べ替えていないときの並び。商品マスタを連携しないテナントはJAN順（理由は mounted）
    defaultSortTarget() {
      return this.hasCol('productCode') ? "" : 'janCode';
    },
    tableSort(headValue) {
      if(headValue == "no") return;
      if(headValue == "adjustementSuuryo") return;
      this.currentPageNo = 1;
      if(this.sortState.target == headValue){
        this.sortState.ascent = !this.sortState.ascent;
      }
      else{
        this.sortState.target = headValue;
        this.sortState.ascent = true;
      }
    },

    reload() {
      api.post("/tanaoroshi/diff/list/search", {
          tanaoroshiId: this.tanaoroshi.tanaoroshiId ?? "",
          shopCode: this.shopCode ?? "",
        }
      )
      .then((response)=>{

        const hasErr = response.data.status == '1';
        const hasData = response.data.results?.diffList?.length ?? 0 != 0;
        const inventoryStatus = response.data.results?.inventoryStatus;
        this.inventoryStatus = inventoryStatus;
        // エラーあり、またはデータが無く保存も不可の場合は、エラーとし、再検索させる
        if(hasErr || (!hasData && !this.canAdjustment)){
          this.dataError = true;
        }
        this.dataList = response.data.results?.diffList ?? [];
        this.htSerialZoneMap = response.data.results?.htSerialZoneMap;
        this.searchDatetime = this.getTimestamp();
        this.inventoryStatus = inventoryStatus;
      })
      .catch((error) => {
        // back previous page
        this.dataError = true;
      })
      this.resetCondition();
    },
    downloadCsv() {
      utils.simpleDownloadCsv(this.headers.filter(e => e != "adjustementSuuryo"),
        this.sortedTable, "tanaoroshiSai.label.table.", 'tanaoroshiSai.fileNamePrefix.csv', this.$t);
    },
    print() {
      window.print();
    },
    // 保存ダイアログ表示
    openAdjustmentDialog() {
      this.adjustmentDialog = {
        tanaoroshiId: this.tanaoroshi.tanaoroshiId,
        siteCode: this.shopCode,
        updateList: this.dataList.filter(e => 'adjustmentQuantity' in e && e['adjustmentQuantity'] != ""),
        searchDatetime: this.searchDatetime
      }
      this.adjustmentDialogVisible = true;
    },
    // 保存ダイアログキャンセル
    confirmAdjustmentCancel() {
      this.adjustmentDialogVisible = false;
    },
    // 保存ダイアログOK押下
    confirmAdjustmentOk() {
      this.reload();
      this.adjustmentDialogVisible = false;
    },
    // 棚卸情報追加ダイアログ表示
    openAddJanDialog() {
      this.addJanDialog = {
        tanaoroshiId: this.tanaoroshi.tanaoroshiId,
        siteCode: this.shopCode,
        searchDatetime: this.searchDatetime,
        htSerialZoneMap: this.htSerialZoneMap,
      }
      this.addJanDialogVisible = true;
    },
    // 棚卸情報追加ダイアログキャンセル
    confirmAddJanCancel() {
      this.addJanDialogVisible = false;
    },
    // 棚卸情報追加ダイアログOK押下
    confirmAddJanOk() {
      this.reload();
      this.addJanDialogVisible = false;
    },
    getTimestamp() {
      const date = new Date();
      return `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, "0")}`
        + `${String(date.getDate()).padStart(2, "0")}${String(date.getHours()).padStart(2, "0")}`
        + `${String(date.getMinutes()).padStart(2, "0")}${String(date.getSeconds()).padStart(2, "0")}`
        + `${String(date.getMilliseconds()).padStart(3, "0")}`;
    },
  },
  created() {
    TableSorter.fetchSizeDisplayOrderMap();
  },
  mounted() {
    this.displayPerPage = utils.getDisplayPerPage(this.$t)
    this.displayPerPageItem = utils.getDisplayPerPageItem(this.$t);

    // 商品マスタを連携しないテナントはJAN順に固定する。
    // サーバーのORDER BYが品番→カラー→サイズの順で、商品マスタが空だと
    // その4つが全てNULLになり並びが不定になるため。
    this.sortState.target = this.defaultSortTarget();
    this.sortState.ascent = true;

    // 棚卸ID は URL（/tanaoroshi/{id}）から取る（2026-10-28）。棚卸画面から来たときは、
    // 選んだ行の情報（ゾーンなど）も使う。URL から直接開いたときは棚卸ID だけで開き、
    // 店舗は DAT 一覧の応答から取ってから差異一覧を読む
    const id = Number(this.$route.params.id);
    if (!id) {
      this.dataError = true;
      return;
    }
    const stored = store.hasTanaoroshiParam() ? store.getTanaoroshi() : null;
    if (stored?.tanaoroshiId == id) {
      this.tanaoroshi = stored;
      this.shopCode = store.getTanaShopCode();
      this.searchCondition.targetZoneCode = store.getTanaZoneCode();
      if(this.tanaoroshi?.zoneList?.at(0)?.zoneCode == this.searchCondition.targetZoneCode) this.searchCondition.targetZoneCode = null;
    } else {
      this.tanaoroshi = { tanaoroshiId: id };
    }
    this.loadDatList().then(() => {
      if (!this.shopCode) {
        this.dataError = true;
        return;
      }
      this.reload();
    });
    
  }
};
</script>

<style>
.section_title {
  font-size: 16px;
  font-weight: bold;
  border-left: 4px solid #3949ab;
  padding-left: 8px;
  margin-bottom: 10px;
}
.tana_info {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 48px;
}
.tana_info_value {
  font-size: 16px;
  padding-left: 12px;
}
.section_note {
  font-size: 13px;
  color: #c62828;
  margin-bottom: 8px;
}
/* Vuetify の既定（薄い文字色・小さめ）に負けるので !important で上書きする */
.section_table thead th {
  background-color: #3949ab !important;
  color: #ffffff !important;
  font-size: 14px !important;
  font-weight: 700 !important;
  letter-spacing: 0.02em !important;
  height: 42px !important;
}
.section_table td {
  font-size: 14px !important;
  color: #212121;
  height: 44px !important;
}
.file_name {
  font-family: "Consolas", "Courier New", monospace;
  font-size: 14px !important;
  font-weight: 600;
  color: #263238;
}
.v-data-table > .v-data-table__wrapper > table > thead > tr > th {
  white-space: nowrap;
}

.print_content {
  display: none;
  font-size: 12px;
  font-weight: bold;
  color: #555;
  text-align: left;
}

.print_head {
  padding-left: 2px;
}

.print_data_row {
  width: 100%;
  display: flex;
  justify-content: flex-start;
  flex-wrap: wrap;
}

.print_head,
.print_data {
  width: 9%;
  padding: 4px 2px;
  border-bottom: 1px solid #ccc;
  word-break: break-all;
}
.print_only {
  display: none !important;
}
@media print {
  .v-main {
    padding: 0 0 0 0 !important;
  }

  .v-btn {
    display: none !important;
  }

  .v-data-table {
    display: none !important;
  }

  .print_col {
    margin: 0;
    padding: 0;
  }

  .print_content {
    display: flex;
    justify-content: flex-start;
    flex-wrap: wrap;
  }
  .no_print {
    display: none !important;
  }
  .print_only {
    display: block !important;
  }
}
</style>
